// ─────────────────────────────────────────────────────────────────────────────
// MANAGEMENT SJT — Question Bank
// 24 scenarios · Most/Least effective response format · 30 min
// Science: Motowidlo (2003) procedural knowledge · Tacit knowledge theory
// Market: Team leader promotions · Management trainee · Assessment centres
// ─────────────────────────────────────────────────────────────────────────────

export const SJT_MANAGEMENT_ITEMS = [
  // ── DELEGATION ────────────────────────────────────────────────────────────
  {
    id:"mg01", d:1, category:"delegation",
    context:"You have just been promoted to team leader with six direct reports. One task that needs completing urgently is well within the capability of your most experienced team member, but she is already at 90% capacity this week.",
    opts:[
      "A: Do the task yourself to avoid overburdening her",
      "B: Ask her directly, acknowledge she is busy, explain the urgency and importance, discuss what could be deprioritised to make space, and delegate with full context",
      "C: Assign the task without any discussion and expect it to be completed",
      "D: Tell your manager the task cannot be done this week due to team capacity",
    ],
    most:1, least:2,
    exp_most:"Transparent delegation with a conversation about priorities respects the team member's time while getting critical work done.",
    exp_least:"Assigning without discussion creates resentment, increases error risk and damages trust early in a leadership relationship."
  },
  {
    id:"mg02", d:2, category:"delegation",
    context:"You have delegated a project to a capable team member. Halfway through, you realise they have taken a significantly different approach than you would have. The approach appears sound but unfamiliar to you.",
    opts:[
      "A: Immediately redirect them to your preferred approach",
      "B: Schedule a check-in, ask them to walk you through their reasoning, share your perspective and, if their approach is valid, allow them to continue while noting what you have learned",
      "C: Let them continue without comment — it is their project now",
      "D: Take over the project yourself to ensure it is done your way",
    ],
    most:1, least:3,
    exp_most:"Coaching through inquiry rather than redirecting arbitrarily builds capability and shows confidence in your team.",
    exp_least:"Taking over a delegated project undermines trust, reduces development opportunity and signals micromanagement."
  },
  {
    id:"mg03", d:3, category:"delegation",
    context:"You need to delegate a high-stakes client presentation to a junior team member who has never done one before. The presentation is in two weeks.",
    opts:[
      "A: Give them the brief and trust them to figure it out",
      "B: Do the presentation yourself — too risky to delegate",
      "C: Delegate clearly, provide a structured brief, schedule rehearsal sessions, give specific feedback, and be available as a backstop while making it clear this is their presentation",
      "D: Assign the task but plan to intervene and take over just before the presentation",
    ],
    most:2, least:3,
    exp_most:"Structured delegation with coaching scaffolding and genuine ownership is the developmental gold standard.",
    exp_least:"Planning a last-minute takeover is the worst outcome — it creates anxiety, removes ownership and teaches nothing."
  },

  // ── PERFORMANCE MANAGEMENT ────────────────────────────────────────────────
  {
    id:"mg04", d:1, category:"performance",
    context:"One of your team members, who has been performing well, has missed three deadlines in the past two weeks. You have not yet spoken to them about it.",
    opts:[
      "A: Wait and see if it resolves itself",
      "B: Send a formal written warning without first having a conversation",
      "C: Have a private, supportive conversation to understand if there are personal or professional challenges, set clear expectations going forward and agree a short-term support plan",
      "D: Raise the performance issue in the next team meeting",
    ],
    most:2, least:3,
    exp_most:"A private, empathetic conversation before any formal action is both fair and more likely to resolve the root cause.",
    exp_least:"Raising an individual's performance issues in a team meeting is humiliating and a serious management failure."
  },
  {
    id:"mg05", d:2, category:"performance",
    context:"You have an underperforming team member who is aware of the performance gap. You have had two informal conversations but no improvement has occurred over six weeks.",
    opts:[
      "A: Continue with informal conversations indefinitely",
      "B: Initiate a formal performance improvement plan (PIP) in line with HR policy, set clear measurable targets, provide regular check-ins and support, and document all discussions",
      "C: Move the team member to a less important project to minimise the impact of their underperformance",
      "D: Ask HR to start a disciplinary process immediately",
    ],
    most:1, least:3,
    exp_most:"Moving to a structured PIP after failed informal intervention is the correct escalation path — fair, documented and supportive.",
    exp_least:"Jumping straight to disciplinary without a PIP skips due process and exposes the organisation to legal risk."
  },
  {
    id:"mg06", d:3, category:"performance",
    context:"A top performer in your team is becoming increasingly difficult — missing meetings, giving short answers to colleagues and pushing back on reasonable requests. Their output is still strong.",
    opts:[
      "A: Ignore the behaviour because their output is too valuable to risk",
      "B: Address it privately and promptly. Acknowledge their value, name the specific behaviours you have observed, listen to understand what is driving it, and discuss what needs to change",
      "C: Tell the team to work around the difficult behaviours",
      "D: Escalate directly to HR without speaking to them first",
    ],
    most:1, least:0,
    exp_most:"Addressing behavioural issues directly, privately and with empathy — separate from performance — is strong management.",
    exp_least:"Ignoring disruptive behaviour because someone is a high performer normalises it and lowers team standards."
  },

  // ── CONFLICT RESOLUTION ───────────────────────────────────────────────────
  {
    id:"mg07", d:1, category:"conflict",
    context:"Two of your team members have had a heated disagreement that is now affecting team morale and collaboration. Both have spoken to you separately and given very different accounts.",
    opts:[
      "A: Take one person's side based on who you have known longer",
      "B: Meet with both team members together, facilitate a structured conversation focused on the work impact and a path forward, and follow up to ensure the situation improves",
      "C: Tell them both to sort it out themselves — adults should resolve conflicts without management involvement",
      "D: Meet with each person separately to listen fully, then bring them together with a clear agenda focused on resolution rather than blame",
    ],
    most:3, least:0,
    exp_most:"Individual listening followed by a facilitated joint conversation is the most thorough approach to mediation.",
    exp_least:"Taking sides without proper investigation is a serious breach of fairness and managerial responsibility."
  },
  {
    id:"mg08", d:2, category:"conflict",
    context:"One of your team members comes to you saying that another team member has been taking credit for her ideas in meetings. She has not yet raised it with the colleague directly.",
    opts:[
      "A: Immediately confront the colleague on her behalf",
      "B: Listen empathetically, validate her concern, coach her on how to address it directly with the colleague, offer to support or mediate if she would like, and monitor the situation",
      "C: Tell her it is politics and she needs to be more assertive",
      "D: Raise it in the next team meeting to address the culture of credit attribution",
    ],
    most:1, least:3,
    exp_most:"Empathetic coaching that builds the team member's agency is more empowering than either dismissing or over-intervening.",
    exp_least:"Raising a specific interpersonal credit issue in a team meeting is inappropriate and likely to worsen the conflict."
  },
  {
    id:"mg09", d:3, category:"conflict",
    context:"There is a growing tension between two sub-teams you manage — the analysts and the operations staff. Each group feels the other doesn't understand or respect their work. It's been building for three months.",
    opts:[
      "A: Let it run its course — tension between teams is natural",
      "B: Speak to the loudest voices in each camp and tell them to be professional",
      "C: Address it structurally: organise cross-team shadowing, create shared goals, hold a facilitated retrospective on the tension, and implement regular joint check-ins",
      "D: Reorganise the teams to reduce interaction",
    ],
    most:2, least:3,
    exp_most:"Structural interventions — shared goals, cross-team exposure, facilitated retrospectives — address root causes not just symptoms.",
    exp_least:"Reducing interaction entrenches siloes and allows the tension to fester — the opposite of resolution."
  },

  // ── TEAM DEVELOPMENT ──────────────────────────────────────────────────────
  {
    id:"mg10", d:1, category:"development",
    context:"You have a team member who is very capable but lacks confidence in presenting to senior stakeholders. An important presentation opportunity has come up in six weeks.",
    opts:[
      "A: Do the presentation yourself to avoid the risk",
      "B: Give the opportunity to someone else who is already confident",
      "C: Give them the opportunity with your full support — coaching sessions, dry runs, constructive feedback and a clear debrief after",
      "D: Tell them this is their chance and leave them to prepare on their own",
    ],
    most:2, least:3,
    exp_most:"Structured stretch opportunity with coaching scaffolding is the hallmark of effective development.",
    exp_least:"Sink-or-swim without support wastes development potential and can damage confidence further."
  },
  {
    id:"mg11", d:2, category:"development",
    context:"During your one-to-one, a team member tells you they are feeling stagnant and are considering leaving unless they see a path forward. Their performance is strong.",
    opts:[
      "A: Tell them to be patient — good things come to those who wait",
      "B: Dismiss the concern — they are lucky to have a job",
      "C: Thank them for being candid. Explore what 'growth' means to them, map out realistic options within the organisation, discuss a development plan and agree on specific milestones and timelines",
      "D: Offer an immediate salary increase to retain them",
    ],
    most:2, least:1,
    exp_most:"Taking the retention signal seriously with a structured career conversation addresses the real issue: direction, not necessarily money.",
    exp_least:"Dismissing a retention concern from a strong performer is a management failure and likely leads to losing them."
  },
  {
    id:"mg12", d:3, category:"development",
    context:"You have identified that your team collectively lacks data analysis skills — a gap that will increasingly affect their performance as the role evolves. Training is expensive and your budget is limited.",
    opts:[
      "A: Wait until next year when the budget may be better",
      "B: Identify the team members with the most aptitude, invest in their development first and create a peer learning structure where they then upskill the rest of the team",
      "C: Hire a new team member with data skills instead of developing existing staff",
      "D: Raise the gap in the team meeting and ask team members to self-learn in their own time",
    ],
    most:1, least:3,
    exp_most:"Seeding capability through peer learning is a cost-effective, sustainable approach to team-wide skill development.",
    exp_least:"Expecting employees to develop critical skills in their own time without support or recognition is exploitative and ineffective."
  },

  // ── DECISION MAKING ───────────────────────────────────────────────────────
  {
    id:"mg13", d:2, category:"decisions",
    context:"Your team must deliver a project in four weeks. Halfway through, it becomes clear that the original timeline was overoptimistic — you need either more resources or a reduced scope. Your manager is travelling and difficult to reach.",
    opts:[
      "A: Keep quiet and push the team to work harder, hoping you make the deadline",
      "B: Proactively reach out to your manager through any available channel, clearly lay out the options, your recommendation and the trade-offs, and ask for a decision — or make a provisional decision within your authority",
      "C: Unilaterally extend the deadline without informing your manager",
      "D: Tell the client the project is on track and deal with the fallout later",
    ],
    most:1, least:3,
    exp_most:"Escalating with a clear problem statement, options and recommendation even to a hard-to-reach manager is the right senior behaviour.",
    exp_least:"Telling clients the project is on track when it isn't is a serious integrity failure."
  },
  {
    id:"mg14", d:3, category:"decisions",
    context:"Your team gives you two different recommendations on an important strategic decision. You are under time pressure. One recommendation comes from your most experienced team member; the other from your least experienced but often most creative team member.",
    opts:[
      "A: Go with the most experienced team member's recommendation automatically",
      "B: Run a quick structured discussion — ask each person to briefly defend their position and identify the key risk in the other's approach, then make a decision and clearly explain your reasoning",
      "C: Make the decision entirely on your own without consulting further",
      "D: Delay the decision until you have more time to analyse it fully",
    ],
    most:1, least:3,
    exp_most:"A rapid structured decision process that draws on both perspectives and makes reasoning transparent is the hallmark of strong manager judgement.",
    exp_least:"Delaying a time-pressured decision is often the worst outcome — inaction has costs."
  },
  {
    id:"mg15", d:2, category:"decisions",
    context:"Your organisation has asked all managers to implement a new process that you believe will slow your team down significantly without clear benefit. Your team are already unhappy about workload.",
    opts:[
      "A: Ignore the directive and continue with the old process",
      "B: Implement the process as directed, communicate the reasons clearly to your team, collect data on the impact and raise concerns through the proper channel with evidence and a specific proposal for improvement",
      "C: Tell your team the new process is management's fault and you disagree with it",
      "D: Implement selectively — follow it when senior management are watching",
    ],
    most:1, least:3,
    exp_most:"Implementing with integrity, communicating clearly, and raising concerns through evidence-based channels is aligned leadership.",
    exp_least:"Selective compliance based on observation is dishonest and toxic to team culture."
  },

  // ── COMMUNICATION & FEEDBACK ──────────────────────────────────────────────
  {
    id:"mg16", d:1, category:"communication",
    context:"You need to give constructive feedback to a team member whose recent work quality has dropped. They are generally sensitive to criticism.",
    opts:[
      "A: Avoid giving the feedback to spare their feelings",
      "B: Give the feedback in front of the team so others hear the standard",
      "C: Give the feedback privately and clearly, focusing on specific behaviours and impact rather than personality, using a compassionate but direct tone, and agreeing on improvement steps together",
      "D: Ask a colleague to deliver the feedback on your behalf",
    ],
    most:2, least:1,
    exp_most:"Private, specific, behaviour-focused feedback with an agreed improvement plan is the compassionate and effective approach.",
    exp_least:"Public feedback on performance issues is humiliating and a management failure."
  },
  {
    id:"mg17", d:2, category:"communication",
    context:"You have just received news of a significant restructure that will affect your team's roles. You are told to communicate it at 5pm today but have been given very limited information yourself.",
    opts:[
      "A: Delay the communication until you have all the answers",
      "B: Deliver the communication at the agreed time. Be honest about what you know and don't know. Share next steps and timelines for further information. Invite questions and acknowledge uncertainty openly",
      "C: Tell your team not to worry — everything will be fine",
      "D: Share the news informally with one trusted team member first to gauge reaction",
    ],
    most:1, least:2,
    exp_most:"Transparent, timely communication that names uncertainty honestly is more trustworthy than delay or false reassurance.",
    exp_least:"False reassurance ('everything will be fine') erodes trust, especially when it turns out not to be true."
  },
  {
    id:"mg18", d:3, category:"communication",
    context:"Your team has just completed a very challenging project with a great outcome. You want to recognise the effort. Some team members contributed significantly more than others.",
    opts:[
      "A: Praise the team collectively and equally to avoid any awkwardness",
      "B: Identify and acknowledge specific contributions privately and publicly in a balanced way — team-level recognition for the overall result and individual acknowledgement for specific exceptional efforts",
      "C: Only recognise the highest contributors and say nothing about the rest",
      "D: Wait for formal performance review time to acknowledge the contribution",
    ],
    most:1, least:3,
    exp_most:"Layered recognition — team for the outcome, individuals for specific contributions — is motivating and fair.",
    exp_least:"Delaying recognition until a formal review removes its motivational impact entirely."
  },

  // ── OPERATIONAL MANAGEMENT ────────────────────────────────────────────────
  {
    id:"mg19", d:2, category:"operations",
    context:"You notice that your team consistently struggles on Monday mornings — low energy, slow starts, confusion about priorities. It has been this way for months.",
    opts:[
      "A: Accept it as a Monday thing — everyone hates Mondays",
      "B: Address it structurally: introduce a brief weekly Monday planning ritual, send a priority summary on Friday afternoons, and check in with team members to understand what is causing the slow start",
      "C: Start emailing team members at 8am on Mondays to push them",
      "D: Raise it in a team meeting and tell them Monday performance is not good enough",
    ],
    most:1, least:2,
    exp_most:"Diagnosing and then structurally addressing the root cause is sustainable management.",
    exp_least:"Pressure emails at 8am on Mondays increase stress without solving the underlying problem."
  },
  {
    id:"mg20", d:3, category:"operations",
    context:"A senior stakeholder has come directly to one of your team members to assign additional work, bypassing you. This has happened twice and is starting to create confusion about priorities.",
    opts:[
      "A: Tell your team member to refuse any requests that don't come through you",
      "B: Speak with the senior stakeholder directly and professionally — acknowledge their need for responsive support, explain the impact of bypassing team management on priorities, and agree a protocol for future requests",
      "C: Let it continue — it is better to keep the stakeholder happy",
      "D: Escalate to your own manager to complain about the stakeholder",
    ],
    most:1, least:2,
    exp_most:"Addressing the bypass directly with the stakeholder, with professionalism and a solution-focus, is the managerially correct response.",
    exp_least:"Ignoring a recurring bypass pattern normalises it and undermines your management authority."
  },
  {
    id:"mg21", d:2, category:"operations",
    context:"Your team has a recurring meeting that most members feel is too long and not useful. You chair the meeting.",
    opts:[
      "A: Keep the meeting as-is — it has always been done this way",
      "B: Gather specific feedback from team members about what is and isn't working, redesign the meeting format based on that input, pilot the new format for four weeks and evaluate",
      "C: Cancel the meeting entirely",
      "D: Ask team members to read a summary instead and only come if they have something to add",
    ],
    most:1, least:0,
    exp_most:"Co-designing the meeting improvement with team input creates buy-in and ensures the redesign addresses real frustrations.",
    exp_least:"Keeping a meeting that everyone finds useless without attempting improvement signals poor operational management."
  },

  // ── MANAGING UP & ACROSS ──────────────────────────────────────────────────
  {
    id:"mg22", d:2, category:"managing_up",
    context:"Your manager has asked you to commit to a delivery timeline that you believe is unrealistic given your team's current workload. They seem very committed to the timeline.",
    opts:[
      "A: Agree to the timeline to avoid conflict and deal with the consequences later",
      "B: Agree immediately without raising concerns",
      "C: Respectfully but clearly raise your concerns with specific evidence — workload data, dependencies, risk assessment — and propose a realistic alternative or a trade-off for your manager to consider",
      "D: Tell your team the timeline is the manager's fault if things go wrong",
    ],
    most:2, least:3,
    exp_most:"Evidence-based pushback with a constructive alternative is the mark of a trusted, senior manager.",
    exp_least:"Deflecting blame to your manager erodes team confidence and undermines your credibility."
  },
  {
    id:"mg23", d:3, category:"managing_up",
    context:"You identify a significant risk in a project your manager is very excited about. You have raised it once in passing and it was brushed off. The risk is material.",
    opts:[
      "A: Drop it — you raised it once and it was not received",
      "B: Document the risk formally, prepare a short structured briefing, request a specific conversation with your manager to present it clearly, and escalate to the next level if still not acted upon",
      "C: Go directly to your manager's manager without informing your manager first",
      "D: Tell team members about the risk so they are not surprised if things go wrong",
    ],
    most:1, least:2,
    exp_most:"Persisting with a material risk through structured escalation — documented, formal, presented clearly — is the right senior behaviour.",
    exp_least:"Bypassing your manager to their manager without first informing them damages the relationship and the chain of trust."
  },
  {
    id:"mg24", d:2, category:"managing_up",
    context:"You have just taken over a team from a previous manager who was very popular. Several team members keep comparing your approach unfavourably to your predecessor's.",
    opts:[
      "A: Try to copy your predecessor's style to win acceptance",
      "B: Acknowledge the transition is challenging for the team, express genuine respect for what worked well before, be transparent about your own approach and the reasoning behind it, and give the team time to adjust while staying consistent",
      "C: Tell team members that comparisons to the previous manager are not helpful and must stop",
      "D: Identify the team members making comparisons and manage them out",
    ],
    most:1, least:3,
    exp_most:"Acknowledging the transition, respecting the predecessor and staying authentically consistent builds trust through the handover period.",
    exp_least:"Trying to manage out people who make comparisons is heavy-handed, creates fear and will damage the team culture."
  },
];
